import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Subject } from 'rxjs';

import { LayerApi } from '../api/layer-api';
import { environment } from '../environment';
import { ESRI } from '../esri-loader-util';
import { DocumentSources, IDocumentSource, ITrafficCameraProperties, MapCoreDocument, MapCoreLayer } from '../types';

@Injectable({
    providedIn: 'root'
})
export class DocumentService {

    public ecsAliases = {
        documentClass: 'Class',
        documentType: 'Type',
        title: 'Title',
        fileName: 'Name',
        mimeType: 'Mime Type',
        source: 'Source',
        size: 'File Size',
        url: 'Url',
        documentId: 'Document Id',
        creationDate: 'Created',
        lastModificationDate: 'Modified'
    };
    private readonly mimeTypeImages = ['image/apng', 'image/avif', 'image/gif', 'image/jpeg', 'image/png', 'image/svg+xml', 'image/webp', 'image/bmp'];

    //sources (notify)
    private readonly fullScreenImagesRequested = new Subject<MapCoreDocument[]>();
    private readonly fullScreenVideoRequested = new Subject<MapCoreDocument>();

    //streams (listen)
    fullScreenImagesRequestedObservable = this.fullScreenImagesRequested.asObservable();
    fullScreenVideoRequestedObservable = this.fullScreenVideoRequested.asObservable();

    constructor(private readonly layerApi: LayerApi, private readonly sanitizer: DomSanitizer) { }

    public async getDocuments(record: any, docSources: IDocumentSource[], layer?: MapCoreLayer): Promise<MapCoreDocument[]> {
        const documents = await Promise.all([
            this.getLayerDocuments(record, layer),
            this.getSourceDocuments(record, docSources)
        ]);
        return documents.flat();
    }

    public async getLayerDocSources(layerIds: number[]) {
        return await this.layerApi.getLayers(layerIds, 'id docSources: layerDocSource { id type url fields template options }');
    }

    public async getTableDocSources(tableId: number[] | number) {
        return await this.layerApi.getTable(tableId, 'id docSources { id type url fields template options }');
    }

    public isImage(document: MapCoreDocument): boolean {
        return document ? document.source === DocumentSources.PHOTO || this.mimeTypeImages.includes(document.mimeType) : false;
    }

    public isVideo(document: MapCoreDocument): boolean {
        return document ? document.source === DocumentSources.VIDEO : false;
    }

    public requestFullScreenImages(documents: MapCoreDocument[]) {
        this.fullScreenImagesRequested.next(documents);
    }

    public requestFullScreenVideo(document: MapCoreDocument) {
        this.fullScreenVideoRequested.next(document);
    }

    private async getSourceDocuments(record: any, docSources: IDocumentSource[]): Promise<MapCoreDocument[]> {
        const documents: MapCoreDocument[] = [];
        for (const docSource of docSources) {
            const options: any = JSON.parse(docSource.options);
            const fields = JSON.parse(docSource.fields);
            if (fields) {
                const source = docSource.type;
                const title = this.getAttributeValue(record.attributes, fields.docTitleField || fields.docVideoField.replace(' ', '_'));
                const description = fields.docDescField ? this.getAttributeValue(record.attributes, fields.docDescField) : null;
                switch (source) {
                    case DocumentSources.ECS:
                        const query = this.getQueryClause(record, fields.docQueryFields, options.query, options.operator);
                        const documentsUrl = `ecs/documents?query=${query}&sourceName=${options.source}`;
                        const results: any[] = await this.layerApi.Api.http2.get(documentsUrl);
                        for (const r of results) {
                            documents.push({
                                source,
                                title: r.titleSubject,
                                fileName: r.originalFileName,
                                mimeType: r.mimeType,
                                url: `${environment.apiBaseUrl}/ecs/document/${r.documentId}?sourceName=${options.source}`,
                                documentId: r.documentId,
                                documentClass: r.documentClass,
                                documentType: r.documentType,
                                size: r.size,
                                creationDate: r.creationDate,
                                lastModificationDate: r.lastModificationDate
                            });
                        }
                        //}
                        break;
                    case DocumentSources.PHOTO:
                        // TODO: RWIS 'IMAGE' column contains a blob that is not being sent down.
                        const photoUrl = this.getAttributeValue(record.attributes, fields.docImageField);
                        const prefix = this.isBase64(photoUrl) ? 'data:image/png;base64,' : '';
                        documents.push({ source, title, description, url: `${prefix}${photoUrl}` });
                        break;
                    case DocumentSources.VIDEO:
                        let thumbnail = this.getAttributeValue(record.attributes, fields.docImageField);
                        let videoUrl: string;
                        let deviceId: string;
                        if (thumbnail && (thumbnail as string).indexOf('thumbs') > -1 && (thumbnail as string).toUpperCase().indexOf('CAM') > -1) {
                            deviceId = thumbnail.substring(thumbnail.toUpperCase().indexOf('CAM'), thumbnail.length);
                        }
                        if (deviceId) {
                            deviceId = deviceId.substring(deviceId.toUpperCase().indexOf('CAM'), deviceId.toUpperCase().indexOf('.'));
                            const documentsVideoUrl = `trafficCamera/getPropertiesById/${deviceId}`;
                            const result: ITrafficCameraProperties = await this.layerApi.Api.http2.post(documentsVideoUrl);
                            videoUrl = result.httpsUrl;
                            thumbnail = result.imageUrl;
                            if (window.environment.indexOf('internal') === -1) {
                                thumbnail = `${environment.apiBaseUrl}/camera/${result.imageUrl.substring(result.imageUrl.indexOf('CAM'))}`;
                            }
                        }
                        documents.push({ source, title, description, url: videoUrl, thumbnail, displayText: this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, this.sanitizer.bypassSecurityTrustResourceUrl(videoUrl)) });
                        break;
                    case DocumentSources.FEATURE:
                        if (docSource.url && fields && fields.docQueryFields) {
                            const [FeatureLayer] = await Promise.all([ESRI.FeatureLayer]);
                            const attachmentLayer = new FeatureLayer({ url: docSource.url });
                            await attachmentLayer.load();
                            const where = this.getQueryClause(record, fields.docQueryFields, options.query, options.operator);
                            const attachmentsByObjectId = await attachmentLayer.queryAttachments({ where });
                            const attachments = Object.keys(attachmentsByObjectId).flatMap((objectId) => attachmentsByObjectId[objectId]);
                            attachments.forEach(a => {
                                documents.push({
                                    source,
                                    title: a.name,
                                    fileName: a.name,
                                    mimeType: a.contentType,
                                    url: a.url,
                                    documentId: a.id,
                                    size: a.size,
                                    parentObjectId: a.parentObjectId,
                                    keywords: a.keywords,
                                    exifInfo: a.exifInfo
                                });
                            });
                            attachmentLayer.destroy();
                        }
                        break;
                    default:
                        break;
                }
            }
        }
        return documents;
    }

    private async getLayerDocuments(record: any, layer): Promise<MapCoreDocument[]> {
        const documents: MapCoreDocument[] = [];
        if (layer && layer.capabilities && layer.capabilities.data && layer.capabilities.data.supportsAttachment) {
            const objectId = this.getAttributeValue(record.attributes, 'objectId');
            const attachments = await layer.queryAttachments({ objectIds: [Number(objectId)] });
            //const attachments = Object.keys(attachmentsByObjectId).flatMap((objectId) => attachmentsByObjectId[objectId]); s
            attachments[objectId].forEach(a => {
                documents.push({
                    source: DocumentSources.FEATURE,
                    title: a.name,
                    fileName: a.name,
                    mimeType: a.contentType,
                    url: a.url,
                    documentId: a.id,
                    size: a.size,
                    parentObjectId: a.parentObjectId,
                    keywords: a.keywords,
                    exifInfo: a.exifInfo
                });
            });
        }
        return documents;
    }

    private getQueryClause(record, docQueryFields, query, operator: string) {
        const clauses = (query) ? [query] : [];
        for (const q of docQueryFields) {
            let value = this.getAttributeValue(record.attributes, q.docQryField);
            const delm = (q.docQryFieldAliasType && q.docQryFieldAliasType === 'number') ? '' : "'";
            if (operator && operator.toLowerCase().trim() === 'like') {
                value = encodeURI(`%${value}%`);
            }
            if (operator && operator.toLowerCase().trim() === 'in') {
                clauses.push(`ANY ${q.docQryFieldAlias} ${operator} (${delm}${value}${delm})`);
            } else {
                clauses.push(`${q.docQryFieldAlias} ${operator ? operator : '='} ${delm}${value}${delm}`);
            }
        }
        return (clauses.length > 1) ? clauses.join(' and ') : clauses[0];
    }

    private getAttributeValue(attributes: { [key: string]: any; columnName: string }[], columnName: string): string {
        const attribute = attributes.find(a => a.columnName.toLowerCase() === columnName.toLowerCase());
        return attribute ? attribute.value : null;
    }

    private isBase64(s: string) {
        //Image may be base64 (example - RWIS weather station images)
        try {
            if (s) {
                atob(s);
                return true;
            } else {
                return false;
            }
        } catch (e) {
            return false;
        }
    }
}
